import { Box } from "@mui/material";
import { ListItem } from "@src/appV2/redesign/system/ui-components";

import { type TimeSlot } from "../../Open/types";
import { ShiftInfoListItemContent } from "./ShiftInfoListItemContent";

interface SelectableShiftInfoListItemProps {
  shiftId: string;
  timeSlot: TimeSlot;
  startIso: string;
  endIso: string;
  payInMinorUnits: number | undefined;
  workplaceTimezone?: string;
  isSelected: boolean;
  isDisabled: boolean;
  isReadOnly?: boolean;
  disabledReason?: string;
  onToggle: (shiftId: string) => void;
  isOrientationShift?: boolean;
}

export function SelectableShiftInfoListItem(props: SelectableShiftInfoListItemProps) {
  const {
    shiftId,
    timeSlot,
    startIso,
    endIso,
    payInMinorUnits,
    workplaceTimezone,
    isSelected,
    isDisabled,
    isReadOnly = false,
    disabledReason,
    onToggle,
    isOrientationShift = false,
  } = props;

  const isNonInteractive = isDisabled || isReadOnly || isOrientationShift;

  return (
    <Box
      component="button"
      disabled={isNonInteractive}
      sx={(theme) => {
        // When selected, a thick border is used to help bring attention to the selected shift.
        // To compensate, we adjust the padding and margin so none of the components move
        // when the border size changes
        const hasThickBorder = isSelected && !isReadOnly;
        return {
          display: "block",
          width: "100%",
          paddingLeft: 5,
          paddingRight: hasThickBorder ? `calc(${theme.spacing(5)} - 2px)` : 5,
          paddingY: 0,
          border: hasThickBorder
            ? `2px solid ${theme.palette.primary.main}`
            : `1px solid ${theme.palette.grey[300]}`,
          margin: hasThickBorder ? "-1px" : 0,
          borderRadius: theme.shape?.borderRadius ?? 2,
          backgroundColor: isReadOnly
            ? theme.palette.background.quaternary
            : theme.palette.common.white,
          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.04)",
          cursor: isNonInteractive ? "default" : "pointer",
          opacity: isDisabled ? 0.5 : 1,
          transition: "border-color 0.15s ease-in-out",
          "&:focus-visible": {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 2,
          },
        };
      }}
      onClick={() => {
        if (!isNonInteractive) {
          onToggle(shiftId);
        }
      }}
    >
      <ListItem>
        <ShiftInfoListItemContent
          timeSlot={timeSlot}
          startIso={startIso}
          endIso={endIso}
          payInMinorUnits={payInMinorUnits}
          workplaceTimezone={workplaceTimezone}
          disabledReason={isDisabled ? disabledReason : undefined}
          iconColor={(theme) => theme.palette.text.primary}
          isOrientationShift={isOrientationShift}
        />
      </ListItem>
    </Box>
  );
}
